import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Divider,
  LinearProgress,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ExpandMoreOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import currency from "currency.js";
import bkstApi from "../../api";
import BDatePicker from "../../common/input/BDatePicker";
import UserAvatar from "../../common/UserAvatar";
import { getQueryParam, getStoreFilterStreets, getStoreLocationIdForFilter } from "../../common/util";
import ItemFilter from "../../planner/component/ItemFilter";
import { UserContext } from "../../context/UserContext";

const MAX_RANGE_DAYS = 31;

const REASON_LABELS = {
  cash_transfer: "Cash Transfer",
  tip_out: "Tip Out",
  petty_cash: "Petty Cash",
  correction: "Correction",
  add_change: "Add Change",
  make_change: "Make Change",
  count_drawer: "Count Drawer",
  other: "Other",
};

const ACTION_LABELS = {
  cash_in: "Add Cash",
  cash_out: "Remove Cash",
  cash_sale: "Cash Sale",
  tip_out: "Tip Out",
  cash_drop: "Cash Drop",
  payout: "Payout",
  no_sale: "No Sale",
};

function getActivityLabel(entry) {
  if (entry?.reason) {
    return REASON_LABELS[entry.reason] || entry.reason;
  }
  return ACTION_LABELS[entry?.action] || entry?.action || "—";
}

function formatActivityAmount(entry) {
  if (entry.amount == null || entry.action === "no_sale") {
    return "—";
  }
  const formatted = currency(entry.amount).format();
  if (["cash_in", "cash_sale"].includes(entry.action)) {
    return `+${formatted}`;
  }
  if (["cash_out", "payout", "tip_out", "cash_drop"].includes(entry.action)) {
    return `-${formatted}`;
  }
  return formatted;
}

function amountColor(entry) {
  if (["cash_in", "cash_sale"].includes(entry.action)) {
    return "#2e7d32";
  }
  if (["cash_out", "payout", "tip_out", "cash_drop"].includes(entry.action)) {
    return "#c62828";
  }
  return "text.primary";
}

function activityDelta(entry) {
  if (entry?.amount == null || entry.action === "no_sale") {
    return 0;
  }
  if (["cash_in", "cash_sale"].includes(entry.action)) {
    return +entry.amount;
  }
  if (["cash_out", "payout", "tip_out", "cash_drop"].includes(entry.action)) {
    return -entry.amount;
  }
  return 0;
}

function getDrawerNetActivity(drawer) {
  return (drawer.activity || []).reduce((sum, entry) => sum + activityDelta(entry), 0);
}

function formatNetActivity(value) {
  const amount = currency(value || 0);
  if (amount.value > 0) {
    return `+${amount.format()}`;
  }
  if (amount.value < 0) {
    return `-${currency(Math.abs(amount.value)).format()}`;
  }
  return amount.format();
}

function netActivityColor(value) {
  const n = currency(value || 0).value;
  if (n > 0) return "#2e7d32";
  if (n < 0) return "#c62828";
  return "text.primary";
}

export default function CashDrawerActivityReport() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");
  const [days, setDays] = useState([]);
  const [posDevices, setPosDevices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [storeFilter, setStoreFilter] = useState({});
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;

  useEffect(() => {
    let [qFrom, qTo] = [
      getQueryParam(location.search, "from"),
      getQueryParam(location.search, "to"),
    ];
    if (!qFrom || !qTo) {
      qTo = moment().format("YYYY-MM-DD");
      qFrom = moment().subtract(7, "days").format("YYYY-MM-DD");
    }
    if (qTo < qFrom) {
      qTo = qFrom;
    }
    search(qFrom, qTo);
  }, [location.search]);

  useEffect(() => {
    if (toDt < fromDt) {
      setToDt("");
    } else if (fromDt && toDt && moment(toDt).diff(moment(fromDt), "days") > MAX_RANGE_DAYS) {
      setToDt("");
    }
  }, [fromDt, toDt]);

  const getPosLabel = useCallback(
    (posId) => {
      if (!posId) return "Unknown drawer";
      const device = posDevices[posId];
      if (device?.firstName) {
        return device.firstName;
      }
      return posId;
    },
    [posDevices]
  );

  const runDateSearch = (e) => {
    e.preventDefault();
    if (!fromDt || !toDt || toDt < fromDt) return;
    if (moment(toDt).diff(moment(fromDt), "days") > MAX_RANGE_DAYS) return;
    navigate(`/report/cash-drawer-activity?from=${fromDt}&to=${toDt}`);
  };

  const visibleDays = useMemo(() => {
    const storeId = getStoreLocationIdForFilter(user, storeFilter?.store?.value);
    if (!storeId) return days;
    return days
      .map((day) => ({
        ...day,
        drawers: (day.drawers || []).filter(
          (drawer) => (posDevices[drawer.posId]?.storeLocationId || "") === storeId
        ),
      }))
      .filter((day) => day.drawers.length > 0);
  }, [days, storeFilter, posDevices, user]);

  const search = (fromDate, toDate) => {
    setLoading(true);
    setError("");
    bkstApi(`/my/report/cash-drawer-activity?fromDt=${fromDate}&toDt=${toDate}`)
      .then((res) => {
        setDays(Array.isArray(res.data.days) ? res.data.days : []);
        setPosDevices(
          Object.fromEntries((res.data.posDevices || []).map((device) => [device.uid, device]))
        );
        setFromDt(fromDate);
        setToDt(toDate);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.response?.data?.error || e.message || "Failed to load cash drawer activity");
        setDays([]);
        setLoading(false);
      });
  };

  return (
    <Box>
      <Container maxWidth="md">
        <Box mt={4}>
          <Typography variant="h1" gutterBottom>
            Cash Drawer Activity
          </Typography>
          <Typography variant="subtitle1" color="textSecondary" gutterBottom>
            Activity across all cash drawers for the selected dates
          </Typography>
          <Box my={3}>
            <form onSubmit={runDateSearch}>
              <Box sx={{ display: "flex", gap: "10px" }}>
                <BDatePicker
                  value={fromDt}
                  variant="filled"
                  label="From Date"
                  onChange={(d) => setFromDt(d)}
                  required
                  autoOk
                  disableFuture
                  hideIcon={true}
                />
                <BDatePicker
                  value={toDt}
                  variant="filled"
                  label="To Date"
                  onChange={(d) => setToDt(d)}
                  required
                  autoOk
                  disableFuture
                  hideIcon={true}
                />
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  style={{ height: "56px" }}
                >
                  <SearchIcon />
                </Button>
              </Box>
            </form>
          </Box>
          {showStoreFilter && days.length > 0 && (
            <Box my={2}>
              <ItemFilter
                config={[{ name: "store", options: storeStreets }]}
                onChange={(o) => setStoreFilter({ ...storeFilter, ...o })}
              />
            </Box>
          )}
        </Box>

        {loading ? (
          <LinearProgress />
        ) : error ? (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        ) : visibleDays.length === 0 ? (
          <Typography color="textSecondary" sx={{ mt: 2 }}>
            No cash drawer activity for this date range
          </Typography>
        ) : (
          visibleDays.map((day) => {
            const dayNetActivity = day.drawers.reduce(
              (sum, drawer) => sum + getDrawerNetActivity(drawer),
              0
            );
            return (
              <Box my={2} key={day.date}>
                <Accordion elevation={0} defaultExpanded={visibleDays.length === 1}>
                  <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                    <Box py={1} sx={{ width: "100%", display: "flex", gap: "3rem" }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="overline" color="textSecondary">
                          {moment(day.date, "YYYY-MM-DD").format("ddd")}
                        </Typography>
                        <Typography variant="h5">
                          {moment(day.date, "YYYY-MM-DD").format("MMM D")}
                        </Typography>
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="overline" color="textSecondary">
                          Net Activity
                        </Typography>
                        <Typography variant="h5" sx={{ color: netActivityColor(dayNetActivity) }}>
                          {formatNetActivity(dayNetActivity)}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                      {day.drawers.map((drawer) => {
                        const drawerNet = getDrawerNetActivity(drawer);
                        const balanceLabel =
                          day.date === moment().format("YYYY-MM-DD") ? "Expected" : "Closing";
                        return (
                        <Box
                          key={`${day.date}-${drawer.posId}`}
                          sx={{
                            backgroundColor: "#F7F7F7",
                            borderRadius: "4px",
                            p: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 2,
                              alignItems: "flex-start",
                              mb: 1.5,
                            }}
                          >
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 0 }}>
                              {drawer.posId ? (
                                <UserAvatar
                                  uid={drawer.posId}
                                  style={{ width: 36, height: 36, marginRight: 0 }}
                                />
                              ) : null}
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="overline" color="textSecondary">
                                  Cash Drawer
                                </Typography>
                                <Typography variant="h6" noWrap>
                                  {getPosLabel(drawer.posId)}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: "flex", gap: 3 }}>
                              <Box>
                                <Typography variant="overline" color="textSecondary">
                                  Opening
                                </Typography>
                                <Typography variant="body1">
                                  {currency(drawer.openingBalance).format()}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="overline" color="textSecondary">
                                  Net Activity
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ color: netActivityColor(drawerNet) }}
                                >
                                  {formatNetActivity(drawerNet)}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="overline" color="textSecondary">
                                  {balanceLabel}
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {currency(drawer.balance).format()}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>

                          <Divider sx={{ mb: 1.5 }} />

                          {!drawer.activity?.length ? (
                            <Typography variant="body2" color="textSecondary">
                              No activity
                            </Typography>
                          ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                              {drawer.activity.map((entry, idx) => (
                                <Box
                                  key={`${entry.ts}-${entry.action}-${idx}`}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      minWidth: 0,
                                      alignItems: "center",
                                    }}
                                  >
                                    {entry.uid ? (
                                      <UserAvatar
                                        uid={entry.uid}
                                        style={{ width: 28, height: 28, marginRight: 0 }}
                                      />
                                    ) : null}
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="body2" noWrap>
                                        {getActivityLabel(entry)}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        display="block"
                                        noWrap
                                      >
                                        {entry.ts ? moment(entry.ts).format("h:mm A") : ""}
                                        {entry.note ? ` · ${entry.note}` : ""}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      whiteSpace: "nowrap",
                                      color: amountColor(entry),
                                    }}
                                  >
                                    {formatActivityAmount(entry)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            );
          })
        )}
      </Container>
    </Box>
  );
}
